import { shopVar } from "@/lib/shopStyles";
import type { GroupedProduct, GroupedProductItem } from "@/lib/TicketApi";
import { Money } from "@/lib/money";
import { useLanguage } from '@/providers/LanguageProvider';
import { useState, useEffect } from 'react';
import { useCart } from '@/providers/CartProvider';
import { InfoCircledIcon, ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import { motion, AnimatePresence } from 'framer-motion';
import React from 'react';
import ProductAvailability from "./ProductAvailability";
import { Fees } from '@/components/shared/Fees';
import { GroupedDiscountLines } from '@/components/shared/GroupedDiscountLines';

interface GroupedProductProps {
    product: GroupedProduct;
}

const GroupedProduct: React.FC<GroupedProductProps> = ({ product }) => {
    const { currentLanguage: lang } = useLanguage();
    const { selectedQuantity } = useCart();
    const cartQuantity = selectedQuantity(product.productId);
    const isInitiallyExpanded = cartQuantity > 0;
    const [isExpanded, setIsExpanded] = useState(isInitiallyExpanded);
    const [openSubDescriptionProductId, setOpenSubDescriptionProductId] = useState<string | null>(null);

    useEffect(() => {
        if (cartQuantity > 0) {
            setIsExpanded(true);
        }
    }, [cartQuantity]);

    const hasDescription = product.description && product.description.trim() !== '';

    return (
        <div
            className="border rounded-md px-4 pt-4 pb-2 mb-4 shadow-md last:mb-0 overflow-hidden"
            style={{ borderColor: shopVar('--shop-borderColor') }}
        >
            <div className="flex items-start border-b border-dashed w-full pb-4" style={{ borderColor: shopVar('--shop-borderColor') }}>
                <div className="flex-grow pr-2">
                    <div className="flex items-center">
                        <h2 className="text-base font-bold mr-2" style={{ color: shopVar('--shop-textColor') }}>{product.name}</h2>
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            aria-expanded={isExpanded}
                            aria-controls={`grouped-product-details-${product.productId}`}
                            className="flex-shrink-0 text-[var(--shop-textColor)] hover:opacity-70 transition-opacity mr-1"
                        >
                            {isExpanded ? <ChevronUpIcon className="w-4 h-4"/> : <ChevronDownIcon className="w-4 h-4"/>}
                        </button>
                    </div>
                    <p className="font-normal text-xs tracking-tight mt-1" style={{ color: shopVar('--shop-textColor') }}>
                        {Money.fromInteger(product.price.amount, product.price.currency).display(lang)}
                        <Fees serviceFees={product.serviceFees} locale={lang} />
                    </p>
                </div>
            </div>
            <AnimatePresence initial={false}>
                {isExpanded && (
                    <motion.div
                        id={`grouped-product-details-${product.productId}`}
                        key="grouped-content"
                        initial="collapsed"
                        animate="open"
                        exit="collapsed"
                        variants={{
                            open: { height: "auto", opacity: 1, paddingTop: '0.5rem', paddingBottom: '0.5rem' },
                            collapsed: { height: 0, opacity: 0, paddingTop: 0, paddingBottom: 0 }
                        }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="text-sm border-b border-dashed overflow-hidden"
                        style={{ borderColor: shopVar('--shop-borderColor') }}
                    >
                        {hasDescription && (
                            <div className="mb-2">
                                <p className="py-1" style={{ color: shopVar('--shop-textColor') }}>{product.description}</p>
                            </div>
                        )}
                        <h3 className="font-semibold text-xs mb-1" style={{ color: shopVar('--shop-textColor') }}>Includes:</h3>
                        <ul className="list-none pl-2 space-y-1">
                            {product.products.map((subProduct: GroupedProductItem, index: number) => {
                                const hasSubDescription = subProduct.description && subProduct.description.trim() !== '';
                                const isSubDescriptionCurrentlyOpen = openSubDescriptionProductId === subProduct.productId;
                                return (
                                    <React.Fragment key={`${subProduct.productId}-${index}`}>
                                        <li className="flex justify-between items-center text-xs py-1">
                                            <div className="flex items-center">
                                                <span style={{ color: shopVar('--shop-textColor') }} className="mr-1">
                                                    {subProduct.quantity}x {subProduct.name}
                                                </span>
                                                {hasSubDescription && (
                                                    <button
                                                        onClick={() => setOpenSubDescriptionProductId(
                                                            isSubDescriptionCurrentlyOpen ? null : subProduct.productId
                                                        )}
                                                        className="flex-shrink-0 text-[var(--shop-textColor)] hover:opacity-70 transition-opacity ml-1"
                                                        aria-expanded={isSubDescriptionCurrentlyOpen}
                                                        aria-controls={`sub-product-description-${subProduct.productId}`}
                                                        aria-label={`More info about ${subProduct.name}`}
                                                    >
                                                        <InfoCircledIcon className="w-3 h-3" />
                                                    </button>
                                                )}
                                            </div>
                                            <span style={{ color: shopVar('--shop-textColor') }}>
                                                {Money.fromInteger(subProduct.price.amount, subProduct.price.currency).display(lang)}
                                                <Fees serviceFees={subProduct.serviceFees} locale={lang} />
                                            </span>
                                        </li>
                                        <AnimatePresence>
                                            {hasSubDescription && isSubDescriptionCurrentlyOpen && (
                                                <motion.div
                                                    id={`sub-product-description-${subProduct.productId}`}
                                                    initial="collapsed"
                                                    animate="open"
                                                    exit="collapsed"
                                                    variants={{
                                                        open: { height: "auto", opacity: 1, marginTop: '0rem', marginBottom: '0.5rem' },
                                                        collapsed: { height: 0, opacity: 0, marginTop: 0, marginBottom: 0 }
                                                    }}
                                                    transition={{ duration: 0.2, ease: "easeInOut" }}
                                                    className="text-xs pl-2 pr-1 overflow-hidden"
                                                >
                                                    <p style={{ color: shopVar('--shop-textColor') }}>{subProduct.description}</p>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </React.Fragment>
                                );
                            })}
                            <GroupedDiscountLines lines={product.discountLines} lang={lang} className="flex justify-between items-center text-xs py-1" />
                        </ul>
                    </motion.div>
                )}
            </AnimatePresence>
            <div className="pt-2">
                <ProductAvailability product={product}/>
            </div>
        </div>
    );
};

export default GroupedProduct; 